import { Component, OnInit, SecurityContext } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { environment } from '@environments/environment';
import { AuthenticationService } from '@app/_services';
import { AppEventService } from '@app/_components/app-event/app-event.service';
import { OwnerLeadProfileService } from '@app/_components/owner-lead-profile/owner-lead-profile.service';
import { TaskService } from '@app/_components/task/task.service';
import { OwnerLeadProfile } from '@app/_components/owner-lead-profile/owner-lead-profile';
import { MessageService } from 'primeng/api';

interface TaskItem {
    taskId?: number;
    subject?: string;
    title?: string;
    dateDue?: string;
    status?: number;
    taskSource?: number;
}

interface AppEvent {
    appEventId?: number;
    eventType?: string;
    eventCategory?: string;
    metadata?: string;
    clickedAt?: string;
}

@Component({
    selector: 'app-bizdev-home',
    templateUrl: './bizdev-home.component.html',
    styleUrls: ['./bizdev-home.component.css'],
    providers: [MessageService],
    standalone: false
})
export class BizdevHomeComponent implements OnInit {

    // Header
    currentUserName = '';
    today = new Date();

    // Sales Productivity Summary
    productivityHtml: SafeHtml = null;
    productivityLoading = false;
    productivityError = false;
    emailLoading = false;

    // Tile A — Follow-ups (open owner-lead tasks)
    followupTasks: TaskItem[] = [];
    followupTasksLoading = false;
    followupTasksError = false;

    // Tile B — Owner Communications (recent owner leads as proxy)
    recentOwnerLeads: OwnerLeadProfile[] = [];
    ownerLeadsLoading = false;

    // Tile C — Recent Proforma Activity
    recentProformaViews: AppEvent[] = [];
    recentProformaSent: AppEvent[] = [];
    proformaActivityLoading = false;
    proformaActivityError = false;

    // Task H — Pipeline Funnel
    funnelStages = [
        { label: 'Prospect',    value: 1, count: 0, color: '#5b9bd5' },
        { label: 'Lead',        value: 2, count: 0, color: '#4472c4' },
        { label: 'Opportunity', value: 3, count: 0, color: '#ed7d31' },
        { label: 'Win',         value: 4, count: 0, color: '#70ad47' },
        { label: 'Loss',        value: 5, count: 0, color: '#c0392b' },
    ];
    funnelLoading = false;
    funnelError = false;
    funnelTotal = 0;

    // Task I — View toggle
    viewMode: 'me' | 'myteam' | 'all' = 'myteam';
    currentUserId: number = null;

    private baseUrl = `${environment.apiUrl}/report-dashboard`;

    readonly quickLinks = [
        { label: 'Homeowner Leads', route: '/owner-lead-profile-list', icon: 'pi pi-users' },
        { label: 'Property Leads', route: '/property-lead-profile-list', icon: 'pi pi-home' },
        { label: 'Proformas', route: '/proforma-list', icon: 'pi pi-file' },
        { label: 'Proforma Templates', route: '/proforma-template-list', icon: 'pi pi-copy' },
        { label: 'Close Deals', route: '/employee-acq-payment', icon: 'pi pi-check-circle' },
        { label: 'Realtor Profiles', route: '/realtor-profile-list', icon: 'pi pi-id-card' },
        { label: 'Sales Pipeline', route: '/bizdev/pipeline', icon: 'pi pi-sitemap' },
        { label: 'BizDev Reports', route: '/biz-dev-dashboard', icon: 'pi pi-chart-bar' },
        { label: 'Ops Dashboard', route: '/bizdev-dashboard', icon: 'pi pi-th-large' },
        { label: 'Manage Lookups', route: '/bizdev/lookups', icon: 'pi pi-list' },
    ];

    constructor(
        private sanitizer: DomSanitizer,
        private http: HttpClient,
        private router: Router,
        private authService: AuthenticationService,
        private appEventService: AppEventService,
        private ownerLeadProfileService: OwnerLeadProfileService,
        private taskService: TaskService,
        private messageService: MessageService,
    ) {}

    ngOnInit(): void {
        const user = this.authService.currentUserValue;
        if (user) {
            const parts = [user.firstName, user.lastName].filter(Boolean);
            this.currentUserName = parts.join(' ') || user.username || '';
            this.currentUserId = (user as any).userId || null;
        }
        this.loadProductivityReport();
        this.loadRecentOwnerLeads();
        this.loadProformaActivity();
        this.loadPipelineFunnel();
        this.loadFollowupTasks();
    }

    setViewMode(mode: 'me' | 'myteam' | 'all'): void {
        this.viewMode = mode;
        this.loadProductivityReport();
    }

    loadProductivityReport(): void {
        this.productivityLoading = true;
        this.productivityError = false;
        // Task I: when "Me" is selected, send agentUserId.
        // Backend filter support is pending — the param is forwarded but may be ignored server-side.
        const body: { agentUserId?: number } = {};
        if (this.viewMode === 'me' && this.currentUserId) {
            body.agentUserId = this.currentUserId;
        }
        this.http.post<any>(`${this.baseUrl}/sales-productivity/view`, body).subscribe({
            next: (res) => {
                this.productivityLoading = false;
                if (res.success && res.html) {
                    this.productivityHtml = this.sanitizer.bypassSecurityTrustHtml(res.html);
                } else {
                    this.productivityError = true;
                }
            },
            error: () => {
                this.productivityLoading = false;
                this.productivityError = true;
            }
        });
    }

    emailProductivityReport(): void {
        this.emailLoading = true;
        this.http.post<any>(`${this.baseUrl}/sales-productivity/test`, {}).subscribe({
            next: (res) => {
                this.emailLoading = false;
                if (res.success) {
                    this.messageService.add({ severity: 'success', summary: 'Sent', detail: res.message || 'Report emailed to you.' });
                } else {
                    this.messageService.add({ severity: 'warn', summary: 'Not Sent', detail: res.error || 'Could not send email.' });
                }
            },
            error: (err) => {
                this.emailLoading = false;
                const msg = err?.error?.error || err?.statusText || 'Server error';
                this.messageService.add({ severity: 'error', summary: 'Error', detail: msg });
            }
        });
    }

    loadRecentOwnerLeads(): void {
        this.ownerLeadsLoading = true;
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        const form = {
            createDateStart: new Date(thirtyDaysAgo.getTime() - thirtyDaysAgo.getTimezoneOffset() * 60000),
            createDateEnd: new Date(this.today.getTime() - this.today.getTimezoneOffset() * 60000),
        };
        this.ownerLeadProfileService.findAllByDate(form).subscribe({
            next: (res: any) => {
                this.ownerLeadsLoading = false;
                const leads = res?.data?.ownerLeadProfiles || [];
                this.recentOwnerLeads = leads.slice(0, 10);
            },
            error: () => {
                this.ownerLeadsLoading = false;
            }
        });
    }

    loadPipelineFunnel(): void {
        this.funnelLoading = true;
        this.funnelError = false;
        this.ownerLeadProfileService.getFunnelCounts().subscribe({
            next: (res: any) => {
                this.funnelLoading = false;
                const rows: any[] = res?.data?.funnelCounts || [];
                const counts: { [key: number]: number } = {};
                let total = 0;
                rows.forEach((r: any) => {
                    const s = Number(r.leadStatus);
                    const c = Number(r.count || 0);
                    if (s) { counts[s] = c; total += c; }
                });
                this.funnelTotal = total;
                this.funnelStages = this.funnelStages.map(stage => ({
                    ...stage,
                    count: counts[stage.value] || 0,
                }));
            },
            error: () => {
                this.funnelLoading = false;
                this.funnelError = true;
            }
        });
    }

    loadFollowupTasks(): void {
        this.followupTasksLoading = true;
        this.followupTasksError = false;
        this.http.get<any>(`${environment.apiUrl}/tasks`, {
            params: { taskSource: '5', size: '10', sort: 'dateDue,asc' }
        }).subscribe({
            next: (res: any) => {
                this.followupTasksLoading = false;
                const all: TaskItem[] = res?.data?.tasks || [];
                this.followupTasks = all.filter(t => t.status !== 0 && t.status !== 4);
            },
            error: () => {
                this.followupTasksLoading = false;
                this.followupTasksError = true;
            }
        });
    }

    funnelConversion(fromIdx: number): string {
        const from = this.funnelStages[fromIdx]?.count || 0;
        const to   = this.funnelStages[fromIdx + 1]?.count || 0;
        if (from === 0) { return '—'; }
        return Math.round((to / from) * 100) + '%';
    }

    funnelBarWidth(stage: { count: number }): string {
        const max = Math.max(...this.funnelStages.map(s => s.count), 1);
        return Math.round((stage.count / max) * 100) + '%';
    }

    loadProformaActivity(): void {
        this.proformaActivityLoading = true;
        this.proformaActivityError = false;
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        const now = new Date();
        const dateFrom = new Date(thirtyDaysAgo.getFullYear(), thirtyDaysAgo.getMonth(), thirtyDaysAgo.getDate())
            .toISOString().split('T')[0];
        const dateTo = new Date(now.getFullYear(), now.getMonth(), now.getDate())
            .toISOString().split('T')[0];

        this.appEventService.findByFilter(0, 15, 'clickedAt', -1, [], null, {}, {
            eventType: 'PROFORMA_VIEW',
            eventCategory: 'BIZDEV',
            dateFrom,
            dateTo,
        }).subscribe({
            next: (res: any) => {
                this.recentProformaViews = res?.data?.appEvents || [];
                this.checkProformaActivityDone();
            },
            error: () => {
                this.proformaActivityError = true;
                this.checkProformaActivityDone();
            }
        });

        this.appEventService.findByFilter(0, 15, 'clickedAt', -1, [], null, {}, {
            eventType: 'PROFORMA_SENT',
            eventCategory: 'BIZDEV',
            dateFrom,
            dateTo,
        }).subscribe({
            next: (res: any) => {
                this.recentProformaSent = res?.data?.appEvents || [];
                this.checkProformaActivityDone();
            },
            error: () => {
                this.proformaActivityError = true;
                this.checkProformaActivityDone();
            }
        });
    }

    private proformaActivityCallsCompleted = 0;

    private checkProformaActivityDone(): void {
        this.proformaActivityCallsCompleted++;
        if (this.proformaActivityCallsCompleted >= 2) {
            this.proformaActivityLoading = false;
        }
    }

    getTaskStatusLabel(status: number): string {
        const found = this.taskService.statuses.find(s => s.value === status);
        return found ? found.name : String(status);
    }

    navigateTo(route: string): void {
        this.router.navigate([route]);
    }

    getLeadStatusLabel(status: number): string {
        const statuses = this.ownerLeadProfileService.leadStatuses;
        const found = statuses.find(s => s.value === status);
        return found ? found.name : String(status);
    }

    extractConfId(metadata: string): string {
        if (!metadata) return '';
        try {
            const obj = JSON.parse(metadata);
            return obj.confId || '';
        } catch {
            return '';
        }
    }

    navigateToProforma(event: any): void {
        this.router.navigate(['/proforma-list']);
    }

    onProductivityHtmlClick(e: MouseEvent): void {
        const target = e.target as HTMLElement;
        const agentEl = target.closest('[data-agent-id]') as HTMLElement | null;
        if (agentEl) {
            const agentId = agentEl.getAttribute('data-agent-id');
            if (agentId) {
                this.router.navigate(['/bizdev/pipeline'], { queryParams: { agentId } });
            }
        }
    }
}
